import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { BusinessSettingsSidebarWrapper } from "@/business-settings/common/business-settings-sidebar-wrapper";
import { getErrorMessage } from "@/core/api/errors";
import { SPIFF_RULE_EVENTS_LABELS } from "@/spiff-rules/constants";
import { useSpiffSidebar } from "@/spiffs/context/spiff-sidebar-context";
import {
  useGetSpiff,
  useBulkDismissSpiffs,
  useReinstateSpiff,
  useBulkApproveSpiffs,
} from "@/spiffs/query";
import { useGetBusinessUser } from "@/user/query";
import {
  SpiffDetailSidebarHeader,
  SpiffDetailSidebarInfo,
  TriggerTypeLabel,
} from "./spiff-detail-sidebar-common";
import { SpiffTriggerDetails } from "./trigger-details";

interface SpiffDetailSidebarProps {
  spiffId: string;
  businessId?: string | null;
}

export const SpiffDetailSidebar = ({
  spiffId,
  businessId,
}: SpiffDetailSidebarProps) => {
  const { resetSidebar } = useSpiffSidebar();

  const { data: spiff, isLoading: isLoadingSpiff } = useGetSpiff(
    businessId,
    spiffId,
  );
  const { data: spiffUser, isLoading: isLoadingSpiffUser } = useGetBusinessUser(
    businessId,
    spiff?.user_id,
  );

  const { mutate: bulkApproveSpiffs, isPending: isApproving } =
    useBulkApproveSpiffs(businessId);
  const { mutate: bulkDismissSpiffs, isPending: isDismissing } =
    useBulkDismissSpiffs(businessId);
  const { mutate: reinstateSpiff, isPending: isReinstating } =
    useReinstateSpiff(businessId);

  const isLoading =
    isLoadingSpiff || !spiff || isLoadingSpiffUser || !spiffUser;
  const dismissed_at = spiff?.dismissed_at;
  const triggerTypeLabel = spiff?.trigger_event
    ? SPIFF_RULE_EVENTS_LABELS[spiff.trigger_event]
    : "";

  const handleApprove = () => {
    bulkApproveSpiffs(
      { spiffIds: [spiffId] },
      {
        onSuccess: () => {
          toast.success("Spiff approved");
          resetSidebar();
        },
        onError: (error) => {
          toast.error("Failed to approve spiff", {
            description: error.message,
          });
        },
      },
    );
  };

  const handleDismiss = () => {
    bulkDismissSpiffs(
      { spiffIds: [spiffId] },
      {
        onSuccess: () => {
          toast.success("Spiff dismissed");
          resetSidebar();
        },
        onError: (error) => {
          toast.error("Failed to dismiss spiff", {
            description: getErrorMessage(error),
          });
        },
      },
    );
  };

  const handleReinstate = () => {
    reinstateSpiff(
      { spiffId },
      {
        onSuccess: () => {
          toast.success("Spiff reinstated");
          resetSidebar();
        },
        onError: (error) => {
          toast.error("Failed to reinstate spiff", {
            description: error.message,
          });
        },
      },
    );
  };

  return (
    <BusinessSettingsSidebarWrapper>
      <SpiffDetailSidebarHeader
        spiff={spiff}
        onClose={resetSidebar}
        onApprove={!dismissed_at ? handleApprove : undefined}
        onDismiss={!dismissed_at ? handleDismiss : undefined}
        onReinstate={dismissed_at ? handleReinstate : undefined}
        isApproving={isApproving}
        isDismissing={isDismissing}
        isReinstating={isReinstating}
        isLoading={isLoading}
      />
      <div className="w-full flex-1 overflow-auto px-6 py-4">
        <Stack gap="md" fullWidth>
          <SpiffDetailSidebarInfo
            technicianName={spiffUser?.display_identification ?? ""}
            amount={spiff?.amount ?? 0}
            submittedAt={spiff?.trigger?.created_at || spiff?.created_at || ""}
            isLoading={isLoading}
          />
          <TriggerTypeLabel label={triggerTypeLabel} isLoading={isLoading} />
          {spiff && <SpiffTriggerDetails spiff={spiff} />}
        </Stack>
      </div>
    </BusinessSettingsSidebarWrapper>
  );
};
